import { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ROUTES } from 'config';
import { UserRole } from 'interfaces';
import { Page404 } from '../Page404';
import { Forbidden } from '../Forbidden';
import { PrivateRoute } from './components';
import { NoDiscountsPage } from '../NoDiscountsPage';
import { CustomLayoutStyleForDiscountPage } from '../Discount/Discount.styles';
import { CustomLayoutStyleForLocationPage } from '../Location/Location.styles';
import { NoActivePage } from '../NoActivePage';

const LocationPage = lazy(() =>
  import('../Location').then(({ Location }) => ({
    default: Location,
  })),
);
const AuthPage = lazy(() =>
  import('../Auth').then(({ Auth }) => ({
    default: Auth,
  })),
);
const MainPage = lazy(() =>
  import('../Main').then(({ Main }) => ({
    default: Main,
  })),
);
const ProfilePage = lazy(() =>
  import('../Profile').then(({ Profile }) => ({
    default: Profile,
  })),
);
const NotificationsPage = lazy(() =>
  import('../Notifications').then(({ Notifications }) => ({
    default: Notifications,
  })),
);
const CategoryPage = lazy(() =>
  import('../Category').then(({ Category }) => ({
    default: Category,
  })),
);
const DiscountPage = lazy(() =>
  import('../Discount').then(({ Discount }) => ({
    default: Discount,
  })),
);
const DiscountEditorPage = lazy(() =>
  import('../DiscountEditor').then(({ DiscountEditor }) => ({
    default: DiscountEditor,
  })),
);
const RequestDiscountPage = lazy(() =>
  import('../RequestDiscount').then(({ RequestDiscount }) => ({
    default: RequestDiscount,
  })),
);
const DiscountRequestListPage = lazy(() =>
  import('../DiscountRequestList').then(({ DiscountRequestList }) => ({
    default: DiscountRequestList,
  })),
);
const DiscountRequestListFilterPage = lazy(() =>
  import('../DiscountRequestListFilters').then(
    ({ DiscountRequestListFilters }) => ({
      default: DiscountRequestListFilters,
    }),
  ),
);
const RolesManagementPage = lazy(() =>
  import('../RolesManagement').then(({ RolesManagement }) => ({
    default: RolesManagement,
  })),
);
const StatisticsPage = lazy(() =>
  import('../Statistics').then(({ Statistics }) => ({
    default: Statistics,
  })),
);
const StatisticsFiltersPage = lazy(() =>
  import('../StatisticsFilters').then(({ StatisticsFilters }) => ({
    default: StatisticsFilters,
  })),
);
const FavoritesPage = lazy(() =>
  import('../Favorites').then(({ Favorites }) => ({
    default: Favorites,
  })),
);
const ArchivePage = lazy(() =>
  import('../Archive').then(({ Archive }) => ({
    default: Archive,
  })),
);
const APlayerPage = lazy(() =>
  import('../APlayer').then(({ APlayer }) => ({
    default: APlayer,
  })),
);
const LatestDealsPage = lazy(() =>
  import('../LatestDeals').then(({ LatestDeals }) => ({
    default: LatestDeals,
  })),
);

export const PageRoot = () => {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path={ROUTES.login} element={<AuthPage />} />
        <Route
          path={ROUTES.location}
          element={
            <PrivateRoute
              customLayoutStyle={CustomLayoutStyleForLocationPage}
              hideLeftBlock
            >
              <LocationPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.main}
          element={
            <PrivateRoute>
              <MainPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.profile}
          element={
            <PrivateRoute>
              <ProfilePage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.favorites}
          element={
            <PrivateRoute>
              <FavoritesPage />
            </PrivateRoute>
          }
        />
        ;
        <Route
          path={ROUTES.profileNotifications}
          element={
            <PrivateRoute
              allowedRoles={[
                UserRole.User,
                UserRole.Mod,
                UserRole.Super,
                UserRole.Admin,
              ]}
            >
              <NotificationsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.category}
          element={
            <PrivateRoute>
              <CategoryPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.discount}
          element={
            <PrivateRoute customLayoutStyle={CustomLayoutStyleForDiscountPage}>
              <DiscountPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.discountEditor}
          element={
            <PrivateRoute
              allowedRoles={[UserRole.Admin, UserRole.Mod, UserRole.Super]}
            >
              <DiscountEditorPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.requestDiscount}
          element={
            <PrivateRoute allowedRoles={[UserRole.User]}>
              <RequestDiscountPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.requestDiscountAll}
          element={
            <PrivateRoute
              allowedRoles={[
                UserRole.Admin,
                UserRole.Mod,
                UserRole.Super,
                UserRole.User,
              ]}
            >
              <DiscountRequestListPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.requestDiscountFilters}
          element={
            <PrivateRoute
              allowedRoles={[UserRole.Admin, UserRole.Mod, UserRole.Super]}
            >
              <DiscountRequestListFilterPage />
            </PrivateRoute>
          }
        />
        <Route path={ROUTES.forbidden} element={<Forbidden />} />
        <Route
          path={ROUTES.noDiscountsInLocation}
          element={<NoDiscountsPage />}
        />
        <Route path={ROUTES.noActive} element={<NoActivePage />} />
        <Route
          path={ROUTES.rolesManagement}
          element={
            <PrivateRoute allowedRoles={[UserRole.Super]}>
              <RolesManagementPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.statistics}
          element={
            <PrivateRoute allowedRoles={[UserRole.Mod, UserRole.Super]}>
              <StatisticsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.statisticsFilters}
          element={
            <PrivateRoute allowedRoles={[UserRole.Mod, UserRole.Super]}>
              <StatisticsFiltersPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.archives}
          element={
            <PrivateRoute allowedRoles={[UserRole.Mod, UserRole.Super]}>
              <ArchivePage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.aPlayer}
          element={
            <PrivateRoute aPlayerStatusRequired>
              <APlayerPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.latestDeals}
          element={
            <PrivateRoute>
              <LatestDealsPage />
            </PrivateRoute>
          }
        />
        <Route path='*' element={<Page404 />} />
      </Routes>
    </Suspense>
  );
};
